import {
  Input,
  Select,
  FormCard,
  FormItem,
  DatePicker,
  TextArea
} from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useTranslation } from 'react-i18next'
import {
  NATIONALITIES,
  LANGUAGES,
  newEthnicities,
  GENDER_IDENTITY_OPTIONS,
  SEX_OPTIONS,
  pregnancyStatusOptions,
  sexualOrientationOptions,
  maritalStatusOptions,
  religionOptions
} from 'common/constants'
import { Text } from '@lmc/ui-kit/src/general'
import { dateFormat, getSelectOptions } from '../../../utils/utils'
import styled from 'styled-components'
import { SupDatePicker } from '../../../styledComponents'
import { Gender, ResidentStatus } from '@lmc/ts-common'
import { Form as AntdForm, Tooltip } from 'antd'
import useFeatureFlags from 'hooks/useFeatureFlags'
import dayjs from 'dayjs'
import { ResidentFetchItemResponse, SexEnum } from '@lmc/validations'

type DemographicFormCardProps = {
  residentAge: number | undefined
  isPro: boolean
  resident?: ResidentFetchItemResponse
}

export const DemographicFormCard = ({
  residentAge,
  isPro,
  resident
}: DemographicFormCardProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const flags = useFeatureFlags({
    flagNames: ['prsb_resident_gender', 'mods_demographics']
  })?.data
  const isPrsbGenderEnabled = flags?.prsb_resident_gender?.enabled
  const isModsDemographicsEnabled = flags?.mods_demographics?.enabled

  const isDeceased = resident?.status === ResidentStatus.deceased
  const deathDateValue = resident?.deathDate
    ? dayjs(resident.deathDate)
    : undefined
  const deathDetails = resident?.deathDetails || ''

  const getGenderOptions = () => {
    const allowedGendersArray = [Gender.male, Gender.female]

    if (isPrsbGenderEnabled) {
      allowedGendersArray.push(Gender.unknown, Gender.unspecified, Gender.other)
    }

    const genderOptions = allowedGendersArray.map((gender) => ({
      value: gender,
      label: t(supConstants[gender])
    }))

    return genderOptions
  }

  const legacyGender = AntdForm.useWatch('gender')
  const modsSex = AntdForm.useWatch(['demographics', 'sex'])

  const isFemale = isModsDemographicsEnabled
    ? modsSex === SexEnum.female
    : legacyGender === Gender.female

  const sortedReligions = religionOptions.sort((a, b) =>
    a.label > b.label ? 1 : -1
  )

  return (
    <FormCard title={t(supConstants.DEMOGRAPHIC)}>
      {!isModsDemographicsEnabled && (
        <FormItem
          label={<Text>{t(supConstants.GENDER)}</Text>}
          name="gender"
          rules={[
            {
              required: true
            }
          ]}
        >
          <Select
            optionRender={(option) => <Text>{option.label}</Text>}
            options={getGenderOptions()}
          />
        </FormItem>
      )}
      <AgeContainer>
        <FormItem
          label={<Text>{t(supConstants.DATE_OF_BIRTH)}</Text>}
          name="dateOfBirth"
          rules={[
            {
              required: true,
              message: `${t(supConstants.PLEASE_ENTER)} ${t(
                supConstants.DATE_OF_BIRTH
              )}`
            }
          ]}
        >
          <SupDatePicker placeholder="" format={dateFormat} />
        </FormItem>
        {residentAge && (
          <BaseText>
            {t(supConstants.AGE)} {residentAge}
          </BaseText>
        )}
      </AgeContainer>
      {isDeceased && (deathDateValue || deathDetails) && (
        <>
          {deathDateValue && (
            <FormItem
              label={<Text>{t(supConstants.DATE_OF_DEATH)}</Text>}
              data-testid="death-details-date"
            >
              <DatePicker value={deathDateValue} format={dateFormat} disabled />
            </FormItem>
          )}
          {deathDetails && (
            <FormItem
              label={<Text>{t(supConstants.DETAILS_OF_DEATH)}</Text>}
              data-testid="death-details-text"
            >
              <TextArea value={deathDetails} disabled rows={2} />
            </FormItem>
          )}
          <HelpText>{t(supConstants.DEATH_DETAILS_EDIT_HINT)}</HelpText>
        </>
      )}
      {isModsDemographicsEnabled && (
        <>
          <FormItem
            label={<Text>{t(supConstants.GENDER_IDENTITY)}</Text>}
            name={['demographics', 'genderIdentity']}
          >
            <Select
              placeholder={t(supConstants.SELECT_GENDER_IDENTITY)}
              optionRender={(option) => <Text>{option.label}</Text>}
              labelRender={(option) => <Text>{option.label}</Text>}
              options={GENDER_IDENTITY_OPTIONS}
            />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.SEX)}</Text>}
            name={['demographics', 'sex']}
          >
            <Select
              placeholder={t(supConstants.SELECT_SEX)}
              optionRender={(option) => <Text>{option.label}</Text>}
              labelRender={(option) => <Text>{option.label}</Text>}
              options={SEX_OPTIONS}
            />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.SEXUAL_ORIENTATION)}</Text>}
            name="sexualOrientation"
          >
            <Select
              labelRender={(option) => <Text>{option.label}</Text>}
              options={sexualOrientationOptions}
              optionRender={(option) => <Text>{option.label}</Text>}
              allowClear
            />
          </FormItem>
        </>
      )}
      {isPro && (
        <>
          <FormItem
            label={<Text>{t(supConstants.MARITAL_STATUS)}</Text>}
            name="maritalStatus"
          >
            <Select
              options={maritalStatusOptions}
              optionRender={(option) => <Text>{option.label}</Text>}
              labelRender={(option) => <Text>{option.label}</Text>}
              allowClear
            />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.BIRTHPLACE)}</Text>}
            name="birthplace"
          >
            <Input maxLength={50} />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.NATIONALITY)}</Text>}
            name="nationalities"
          >
            <Select options={getSelectOptions(NATIONALITIES)} mode="tags" />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.LANGUAGES_SPOKEN)}</Text>}
            name="languagesSpoken"
          >
            <Select options={getSelectOptions(LANGUAGES)} mode="tags" />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.ETHNICITY)}</Text>}
            name="ethnicity"
          >
            <Select
              optionRender={(option) => <Text>{option.label}</Text>}
              options={newEthnicities}
            />
          </FormItem>
          <FormItem
            label={<Text>{t(supConstants.RELIGION)}</Text>}
            name="religion"
          >
            <Select
              showSearch
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              labelRender={(option) => <Text>{option.label}</Text>}
              options={sortedReligions}
              optionRender={(option) => <Text>{option.label}</Text>}
              allowClear
            />
          </FormItem>
          {isFemale && (
            <FormItem
              label={<Text>{t(supConstants.PREGNANCY_STATUS)}</Text>}
              name="pregnancyStatus"
            >
              <Select
                labelRender={(option) => <Text>{option.label}</Text>}
                options={pregnancyStatusOptions}
                optionRender={(option) => <Text>{option.label}</Text>}
                allowClear
              />
            </FormItem>
          )}
        </>
      )}
    </FormCard>
  )
}

const AgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`

const BaseText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const HelpText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
  font-size: 13px;
  font-style: italic;
  margin-bottom: 16px;
`
